// PARITY: layer freshness mirrors src/views/MapPortal/ExpandedMapView/index.tsx (layer:status + backstop) — re-check when that flow changes
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MapPortalService from '../services/MapPortal/mapportal.service';
import AssetManagerService from '../services/AssetManager/assetManager.service';
import { useAuth } from '../context/AuthContext';
import { onSocketEvent, subscribeRoom } from '../services/socket.service';
import { QueryKeys } from '../enums/queryKeys';
import { commonMapQueryKey, DEFAULT_TOOL_COLOR, LAYER_PROCESSING_REFETCH_MS } from '../views/CommonMap/constants';
import type { LayerGroup } from '../views/MapPortal/Legend/LegendDrawer';
import type { MapDefaultPosition, MapLayer, MapToolbox, MapToolSetting } from '../types/CommonMapTypes';

const NO_LAYERS: MapLayer[] = [];
const NO_GROUPS: LayerGroup[] = [];

// PARITY: mirrors the legend-order re-emit in src/views/MapPortal/Legend/LegendDrawer.tsx — re-check when that flow changes
// Each group's layers in group order, then ungrouped ones, so map z-order follows the legend.
const orderByLegend = (layers: MapLayer[], groups: LayerGroup[]) => {
  if (!groups.length) return layers;
  const groupIds = new Set(groups.map((group) => group.id));
  const ordered = groups.flatMap((group) => layers.filter((layer) => layer.groupId === group.id));
  return [...ordered, ...layers.filter((layer) => layer.groupId == null || !groupIds.has(layer.groupId))];
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// The layer fields the Asset Manager's layer list is built from (its name follows the title).
const toLayerSignature = (rows: unknown) =>
  Array.isArray(rows) ? rows.map((row: MapLayer) => [row.id, row.publishStatus, row.layerTitle, row.uniqueLayerName].join('|')).join(',') : undefined;

const isProcessingLayer = (layer: unknown) => isRecord(layer) && layer.publishStatus === 'processing';

const toDefaultPosition = (mapDetail: unknown): MapDefaultPosition | undefined => {
  if (!isRecord(mapDetail) || !isRecord(mapDetail.map) || !isRecord(mapDetail.map.general)) return undefined;
  const position = mapDetail.map.general.defaultMapPosition;
  if (!isRecord(position) || !isRecord(position.coordinates)) return undefined;
  return position.coordinates as MapDefaultPosition;
};

const toToolSetting = (value: unknown, fallbackName: string): MapToolSetting => {
  const setting = isRecord(value) ? value : {};
  return {
    // The Infrastructure Manager map always offers every tool, whatever its saved toolbox says.
    enabled: true,
    name: typeof setting.name === 'string' && setting.name.trim() ? setting.name : fallbackName,
    color: typeof setting.color === 'string' && setting.color ? setting.color : DEFAULT_TOOL_COLOR,
  };
};

// PARITY: mirrors the toolbox fallbacks in src/views/MapPortal/ExpandedMapView/index.tsx — re-check when that flow changes
const toToolbox = (mapDetail: unknown): MapToolbox => {
  const toolbox = isRecord(mapDetail) && isRecord(mapDetail.map) && isRecord(mapDetail.map.toolbox) ? mapDetail.map.toolbox : {};
  return { queryTool: toToolSetting(toolbox.queryTool, 'Query Tool'), proximityTool: toToolSetting(toolbox.proximityTool, 'Proximity Tool') };
};

const useInfrastructureMapId = () => {
  const { selectedClient } = useAuth();
  const { getStatus } = AssetManagerService();

  // Already cached by AssetManagerGuard, which gates every route that mounts this map.
  const { data: status, isLoading } = useQuery(getStatus(selectedClient?.id));
  return { mapId: status?.assetMapId ?? undefined, isLoading };
};

// Shared by the map and by anything styled from its layers, so they use one cache entry.
const useMapLayerRows = (mapId: number | undefined) => {
  const { getMapLayers } = MapPortalService();
  const layersQuery = getMapLayers(mapId);
  return useQuery({
    ...layersQuery,
    queryKey: commonMapQueryKey(layersQuery.queryKey),
    enabled: mapId !== undefined,
    // Backstop for a missed `layer:status` event; stops once nothing is processing.
    refetchInterval: (query) => (Array.isArray(query.state.data) && query.state.data.some(isProcessingLayer) ? LAYER_PROCESSING_REFETCH_MS : false),
  });
};

// The map's layers by id, for modules that draw on the map to match how its layers look.
export const useInfrastructureMapLayers = () => {
  const { mapId } = useInfrastructureMapId();
  const { data: layerRows } = useMapLayerRows(mapId);
  return useMemo(() => new Map((Array.isArray(layerRows) ? (layerRows as MapLayer[]) : NO_LAYERS).map((layer) => [layer.id, layer])), [layerRows]);
};

export const useInfrastructureMap = () => {
  const { getMapById, getLayerGroups } = MapPortalService();
  const { mapId, isLoading: isStatusLoading } = useInfrastructureMapId();
  const queryClient = useQueryClient();

  const mapDetailQuery = getMapById(String(mapId));
  const { data: mapDetail, isLoading: isMapLoading } = useQuery({
    ...mapDetailQuery,
    queryKey: commonMapQueryKey(mapDetailQuery.queryKey),
    enabled: mapId !== undefined,
  });
  const { data: layerRows, isLoading: isLayersLoading, refetch: refetchLayers } = useMapLayerRows(mapId);

  const groupsQuery = getLayerGroups(mapId);
  const { data: groupRows, refetch: refetchGroups } = useQuery({
    ...groupsQuery,
    queryKey: commonMapQueryKey(groupsQuery.queryKey),
    enabled: mapId !== undefined,
  });

  // Counterpart of Map Portal's onLayerUpdateComplete: re-reads layers and groups after a layer flow changes them.
  const refreshMap = useCallback(async () => {
    await Promise.all([refetchLayers(), refetchGroups()]);
  }, [refetchLayers, refetchGroups]);

  // The layers the asset list and statistics offer follow the map's own as they are added, renamed or finish processing,
  // however they were refetched. Legend visibility and order edits don't touch the signature, and the first load only records it.
  const layerSignature = useMemo(() => toLayerSignature(layerRows), [layerRows]);
  const previousSignatureRef = useRef<string>();
  useEffect(() => {
    const previous = previousSignatureRef.current;
    previousSignatureRef.current = layerSignature;
    if (previous !== undefined && layerSignature !== undefined && previous !== layerSignature) {
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ASSET_MANAGER_META_LAYERS] });
    }
  }, [layerSignature, queryClient]);

  useEffect(() => {
    if (mapId === undefined) return;
    const leaveRoom = subscribeRoom('map:subscribe', { mapId }, 'map:unsubscribe');
    const off = onSocketEvent('layer:status', () => {
      void refetchLayers();
    });
    return () => {
      off();
      leaveRoom();
    };
  }, [mapId, refetchLayers]);

  const defaultPosition = useMemo(() => toDefaultPosition(mapDetail), [mapDetail]);
  const toolbox = useMemo(() => toToolbox(mapDetail), [mapDetail]);
  const groups: LayerGroup[] = Array.isArray(groupRows) ? groupRows : NO_GROUPS;
  const layers = useMemo(() => orderByLegend(Array.isArray(layerRows) ? layerRows : NO_LAYERS, groups), [layerRows, groups]);

  return {
    mapId,
    defaultPosition,
    layers,
    groups,
    toolbox,
    refreshMap,
    isLoading: isStatusLoading || (mapId !== undefined && (isMapLoading || isLayersLoading)),
  };
};
